import { useTheme } from '@mui/material';
import {
  ChevronIcon,
  ConfirmModal,
  DeleteButton,
  DownloadButton,
  Heading3,
  TextM,
  TTableHeadCell,
  Upload,
} from '../../../../../../app/components';
import { TRegisterBusinessDataRequest } from '../../../../context';
import {
  LegalPersonViewBoxContainer,
  LegalPersonViewBoxTableContainer,
  LegalPersonViewPageSection,
} from './styles';
import { useCallback, useEffect, useState } from 'react';
import {
  TableBodyFile,
  TableFilesWrapper,
  TableHeadFile,
  UploadArea,
} from '../../../LegalPersonHandler/steps/styles';
import { useUploadBusinessFiles } from '../../../../hooks';
import { formatDateFromBackend } from '../../../../../../app/helpers/dateHelpers';
import { IFile } from '../../../../hooks/useUploadBusinessFiles';
import { usePermify } from 'modules/auth/context';
import { EAccountPermissions } from 'modules/auth/context/permify/permify.interfaces';
import { Table } from 'app/components';
import { BusinessDocumentTypeLabel } from 'app/helpers/documentTypes';
import { iconByFileType } from 'modules/customer/components/DocumentsModal/DocumentsModal';

type ILegalPersonViewDocumentsSectionProps = {
  business: TRegisterBusinessDataRequest;
};

const tableBodyFileStyle = {
  display: 'flex',
};

const tableHeadRaw = {
  id: '1',
  content: ['Arquivos', 'Tipo', 'Criado em', 'Baixar', 'Excluir'],
};

type BusinessDocumentTypeLabelType = typeof BusinessDocumentTypeLabel;

const LegalPersonViewDocumentsSection = ({
  business,
}: ILegalPersonViewDocumentsSectionProps) => {
  const theme = useTheme();
  const [tableHeadContent, setTableHeadContent] = useState<
    TTableHeadCell[] | null
  >(null);
  const [isConfirmModalOpen, toggleConfirmModalOpen] = useState<IFile | false>(
    false,
  );
  const { isProfileAuthorized } = usePermify();
  const {
    handleUploadFiles,
    handleDeleteBusinessFile,
    uploadedFiles,
    uploadedFilesLoading,
    fetchBusinessFile,
    downloadBusinessFile,
    deleteRequestLoading,
  } = useUploadBusinessFiles();

  const onDeleteDocument = useCallback(() => {
    if (isConfirmModalOpen !== false && business.id) {
      handleDeleteBusinessFile(
        isConfirmModalOpen.id as string,
        business.id,
      ).finally(() => {
        toggleConfirmModalOpen(false);
      });
    }
  }, [isConfirmModalOpen, business]);

  useEffect(() => {
    const parsedHeadContent = tableHeadRaw.content.map((item, index) => {
      if (index === 0) {
        return {
          id: String(index),
          content: (
            <TableHeadFile>
              <TextM weight="Bold">Arquivo</TextM>
              <ChevronIcon />
            </TableHeadFile>
          ),
        };
      } else {
        return {
          id: String(index),
          content: <TextM weight="Bold">{item}</TextM>,
        };
      }
    });

    fetchBusinessFile(business.id as string);
    setTableHeadContent(parsedHeadContent);
  }, []);

  const handleDrop = (acceptedFiles: File[]) => {
    if (business.id) handleUploadFiles(acceptedFiles, business.id);
  };

  return (
    <LegalPersonViewPageSection>
      <Heading3 style={{ color: theme.palette.brand.secondary.base as string }}>
        Arquivos
      </Heading3>
      {uploadedFiles && uploadedFiles.length > 0 ? (
        <LegalPersonViewBoxTableContainer>
          <TableFilesWrapper>
            <Table
              tableHeadCell={
                isProfileAuthorized(EAccountPermissions.UPDATE_PERSON_BUSINESS)
                  ? tableHeadContent
                  : tableHeadContent?.slice(0, -1)
              }
              tableBodyContent={uploadedFiles.map((item, index) => {
                const cells = [
                  {
                    id: '1',
                    content: (
                      <TableBodyFile style={tableBodyFileStyle}>
                        <div>
                          {
                            iconByFileType[
                              item?.file_extension?.toLocaleLowerCase() || 'pdf'
                            ]
                          }
                        </div>
                        <TextM weight="Semibold">
                          {item?.file_name ?? 'Arquivo'}
                        </TextM>
                      </TableBodyFile>
                    ),
                  },
                  {
                    id: '2',
                    content: (
                      <TextM weight="Regular">
                        {
                          BusinessDocumentTypeLabel[
                            (item?.type ||
                              'OTHER') as keyof BusinessDocumentTypeLabelType
                          ]
                        }
                      </TextM>
                    ),
                  },
                  {
                    id: '3',
                    content: (
                      <TextM weight="Regular">
                        {formatDateFromBackend(
                          item.created_at as string,
                        ).toLocaleDateString('pt-BR')}
                      </TextM>
                    ),
                  },
                  {
                    id: '4',
                    content: (
                      <DownloadButton
                        handleClick={() =>
                          downloadBusinessFile(
                            business.id as string,
                            item.id as string,
                            item,
                          )
                        }
                      />
                    ),
                  },
                  {
                    id: '5',
                    content: (
                      <DeleteButton
                        handleClick={() => toggleConfirmModalOpen(item)}
                      />
                    ),
                  },
                ];
                return {
                  id: String(index),
                  cells: isProfileAuthorized(
                    EAccountPermissions.UPDATE_PERSON_BUSINESS,
                  )
                    ? cells
                    : cells?.slice(0, -1),
                };
              })}
            />
          </TableFilesWrapper>
        </LegalPersonViewBoxTableContainer>
      ) : (
        <LegalPersonViewBoxContainer>-</LegalPersonViewBoxContainer>
      )}
      {isProfileAuthorized(EAccountPermissions.UPDATE_PERSON_BUSINESS) && (
        <UploadArea>
          <Upload handleDrop={handleDrop} loading={uploadedFilesLoading} />
        </UploadArea>
      )}
      <ConfirmModal
        isOpen={isConfirmModalOpen !== false}
        title="Exclusão de arquivo"
        color="danger"
        btnConfirmText="Excluir"
        isLoading={deleteRequestLoading}
        handleConfirm={onDeleteDocument}
        handleClose={() => toggleConfirmModalOpen(false)}
        handleCancel={() => toggleConfirmModalOpen(false)}
      >
        Essa ação é irreversivel, você tem certeza que deseja excluir o arquivo?
      </ConfirmModal>
    </LegalPersonViewPageSection>
  );
};

export default LegalPersonViewDocumentsSection;
